import { useEffect, useRef, useState } from "react";
import { advanceWeatherFade, WEATHER_FADE_MS, weatherFadePhase } from "../lib/weather-transition";
import type { AtmosphericFogLevel } from "../types";

interface Props {
  level: AtmosphericFogLevel;
  bounds: { left: number; top: number; width: number; height: number };
  weatherMask?: string;
  paused?: boolean;
}

const LEVELS: Record<Exclude<AtmosphericFogLevel, "off">, { density: number; speed: number; scale: number; coverage: number; haze: number }> = {
  // Fog should feel like a slow bank drifting across the table rather than
  // weather particles. Keep the heavier levels slightly more active while
  // making every level calm enough for long projector sessions.
  light: { density: 0.24, speed: 0.30, scale: 2.05, coverage: 0.36, haze: 0.020 },
  medium: { density: 0.39, speed: 0.38, scale: 2.38, coverage: 0.49, haze: 0.034 },
  heavy: { density: 0.56, speed: 0.46, scale: 2.72, coverage: 0.62, haze: 0.052 },
};

const VERTEX_SHADER = `
attribute vec2 aPosition;
varying vec2 vUv;
void main() {
  vUv = aPosition * 0.5 + 0.5;
  gl_Position = vec4(aPosition, 0.0, 1.0);
}`;

const FRAGMENT_SHADER = `
precision mediump float;
varying vec2 vUv;
uniform vec2 uResolution;
uniform float uTime;
uniform float uDensity;
uniform float uScale;
uniform float uCoverage;
uniform float uHaze;

float tri(float x) {
  return abs(fract(x) - 0.5);
}

vec3 tri3(vec3 p) {
  return vec3(
    tri(p.z + tri(p.y)),
    tri(p.z + tri(p.x)),
    tri(p.y + tri(p.x))
  );
}

// WebGL translation of the two-octave height-fog technique demonstrated by
// three.js webgpu_custom_fog. The third coordinate supplies a stable virtual
// elevation field for TBLTOP's top-down maps.
float triNoise3D(vec3 p, float speed, float time) {
  float z = 1.4;
  float result = 0.0;
  vec3 base = p;
  for (int octave = 0; octave < 4; octave++) {
    vec3 gradient = tri3(base * 2.0);
    p += gradient + time * 0.1 * speed;
    base *= 1.8;
    z *= 1.5;
    p *= 1.2;
    result += tri(p.z + tri(p.x + tri(p.y))) / z;
    base += 0.14;
  }
  return result;
}

void main() {
  vec2 aspect = vec2(uResolution.x / max(uResolution.y, 1.0), 1.0);
  vec2 world = (vUv - 0.5) * aspect * uScale;

  // A fixed, low-frequency virtual height field lets the fog pool instead of
  // washing the entire top-down map uniformly. The moving noise only changes
  // the upper edge of each bank, so the effect stays anchored to the map.
  float elevationA = triNoise3D(vec3(world * 0.58, 2.7), 0.0, 0.0);
  float elevationB = triNoise3D(vec3(world * 0.31 + 4.2, 6.1), 0.0, 0.0);
  float elevation = clamp((elevationA * 0.64 + elevationB * 0.36 - 0.16) * 1.72, 0.0, 1.0);

  // Advect two banks in different directions. This is intentionally separate
  // from the noise evolution so the fog visibly travels across a top-down map
  // instead of merely boiling in place.
  vec2 drift = vec2(uTime * 0.082, uTime * 0.031);
  float fogNoiseA = triNoise3D(vec3((world + drift) * 1.08, 0.3), 0.28, uTime);
  float fogNoiseB = triNoise3D(vec3((world - drift * 0.72) * 2.16 + 5.4, 3.8), 0.24, uTime * 1.2);
  float fogNoise = fogNoiseA + fogNoiseB;
  float movingTop = uCoverage + (fogNoise - 0.62) * 0.78;
  float pooled = 1.0 - smoothstep(movingTop - 0.16, movingTop + 0.15, elevation);

  float wisp = smoothstep(0.20, 0.72, fogNoise);
  float generalHaze = uHaze * (0.68 + wisp * 0.46);
  float alpha = clamp(pooled * uDensity * (0.68 + wisp * 0.38) + generalHaze, 0.0, 0.62);
  vec3 lowFog = vec3(0.61, 0.66, 0.67);
  vec3 litFog = vec3(0.79, 0.83, 0.81);
  vec3 fogColor = mix(lowFog, litFog, clamp(wisp * 0.82 + (1.0 - elevation) * 0.18, 0.0, 1.0));
  gl_FragColor = vec4(fogColor * alpha, alpha);
}`;

function compile(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("Unable to create atmospheric fog shader.");
  gl.shaderSource(shader, source); gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) ?? "Atmospheric fog shader failed to compile.";
    gl.deleteShader(shader); throw new Error(message);
  }
  return shader;
}

export function AtmosphericFog({ level, bounds, weatherMask, paused = false }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const levelRef = useRef(level); levelRef.current = level;
  const enabled = level !== "off";
  const [present, setPresent] = useState(enabled);
  const redrawRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (enabled) { setPresent(true); return; }
    if (paused) { setPresent(false); return; }
    const timeout = window.setTimeout(() => setPresent(false), WEATHER_FADE_MS + 250);
    return () => window.clearTimeout(timeout);
  }, [enabled, paused]);

  useEffect(() => {
    if (!present || !canvasRef.current) return;
    const canvas = canvasRef.current;
    // The DM preview deliberately renders one static frame. Preserve it after
    // compositing; the animated Player Display still redraws at its normal rate.
    const gl = canvas.getContext("webgl", { alpha: true, antialias: false, depth: false, stencil: false, preserveDrawingBuffer: true, powerPreference: "low-power" });
    if (!gl) { canvas.dataset.fogRenderer = "unavailable"; return; }
    canvas.dataset.fogRenderer = "webgl";

    let vertex: WebGLShader; let fragment: WebGLShader;
    try {
      vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
      fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    } catch (error) {
      console.warn("Atmospheric fog is unavailable:", error);
      return;
    }
    const program = gl.createProgram(); const buffer = gl.createBuffer();
    if (!program || !buffer) { gl.deleteShader(vertex); gl.deleteShader(fragment); return; }
    gl.attachShader(program, vertex); gl.attachShader(program, fragment); gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.warn("Atmospheric fog is unavailable:", gl.getProgramInfoLog(program));
      gl.deleteBuffer(buffer); gl.deleteProgram(program); gl.deleteShader(vertex); gl.deleteShader(fragment); return;
    }

    const position = gl.getAttribLocation(program, "aPosition");
    const resolution = gl.getUniformLocation(program, "uResolution");
    const time = gl.getUniformLocation(program, "uTime");
    const density = gl.getUniformLocation(program, "uDensity");
    const scale = gl.getUniformLocation(program, "uScale");
    const coverage = gl.getUniformLocation(program, "uCoverage");
    const haze = gl.getUniformLocation(program, "uHaze");
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    gl.useProgram(program); gl.enableVertexAttribArray(position); gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.clearColor(0, 0, 0, 0);

    const startedAt = performance.now(); let renderedFrames = 0; let frame = 0; let frameTimer: number | undefined; let stopped = false; let transitionAt = startedAt;
    let fogFade = paused ? (level === "off" ? 0 : 1) : 0;
    let lastLevel: Exclude<AtmosphericFogLevel, "off"> = level === "off" ? "light" : level;
    const schedule = () => {
      if (paused || stopped) return;
      // Slow atmospheric movement reads naturally at 16 fps and is much less
      // expensive than waking the main thread every display refresh.
      frameTimer = window.setTimeout(() => { frameTimer = undefined; frame = requestAnimationFrame(render); }, 1000 / 16);
    };
    const render = (now: number) => {
      if (stopped) return;
      const elapsed = Math.min(100, Math.max(0, now - transitionAt)); transitionAt = now;
      // Fog is intentionally soft, so a reduced drawing buffer saves substantial
      // GPU work on 4K projectors and high-DPI tablets without reducing its quality.
      const maxBufferSide = 1920;
      const ratio = Math.max(0.1, Math.min(window.devicePixelRatio || 1, 0.75, maxBufferSide / Math.max(canvas.clientWidth, canvas.clientHeight, 1)));
      const width = Math.max(1, Math.round(canvas.clientWidth * ratio));
      const height = Math.max(1, Math.round(canvas.clientHeight * ratio));
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; gl.viewport(0, 0, width, height); }
      const currentLevel = levelRef.current; if (currentLevel !== "off") lastLevel = currentLevel;
      fogFade = paused ? (currentLevel === "off" ? 0 : 1) : advanceWeatherFade(fogFade, currentLevel !== "off", elapsed);
      const settings = LEVELS[lastLevel];
      gl.clear(gl.COLOR_BUFFER_BIT); gl.useProgram(program);
      gl.uniform2f(resolution, width, height);
      gl.uniform1f(time, ((now - startedAt) / 1000) * settings.speed);
      gl.uniform1f(density, settings.density * fogFade); gl.uniform1f(scale, settings.scale);
      gl.uniform1f(coverage, settings.coverage); gl.uniform1f(haze, settings.haze * fogFade);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      renderedFrames += 1; canvas.dataset.renderedFrame = "true";
      canvas.dataset.fogFrameCount = String(renderedFrames);
      canvas.dataset.fogTime = (((now - startedAt) / 1000) * settings.speed).toFixed(3);
      canvas.dataset.fogFade = fogFade.toFixed(3); canvas.dataset.fogTransition = weatherFadePhase(fogFade, currentLevel !== "off");
      schedule();
    };
    if (paused) render(performance.now() + 33);
    else schedule();
    redrawRef.current = () => render(performance.now() + 33);

    return () => {
      redrawRef.current = () => undefined; stopped = true; cancelAnimationFrame(frame); if (frameTimer !== undefined) clearTimeout(frameTimer);
      gl.deleteBuffer(buffer); gl.deleteProgram(program); gl.deleteShader(vertex); gl.deleteShader(fragment);
    };
  }, [paused, present]);
  useEffect(() => { if (enabled && paused) redrawRef.current(); }, [bounds.height, bounds.width, enabled, level, paused]);

  if (!present) return null;
  return <canvas ref={canvasRef} className="atmospheric-fog-canvas" style={{ ...bounds, maskImage: weatherMask, WebkitMaskImage: weatherMask, maskSize: "100% 100%", WebkitMaskSize: "100% 100%" }} data-atmospheric-fog={level} data-weather-mask={weatherMask ? "active" : "none"} data-fog-model="pooled-height" data-fog-motion="advected-layered-noise" data-weather-fade-ms="900" data-weather-transition-mode="fade" data-animated={paused ? "false" : "true"} aria-hidden="true" />;
}
